import React, { useEffect, useState, useMemo } from 'react';
import {
  Box, Typography, Container, useTheme, useMediaQuery,
  MenuItem, Select, FormControl, Grid, InputLabel, Paper
} from '@mui/material';
import api from '../../services/api';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts';

const LIMIT_OPTIONS = [5, 10, 15, 'all'];

const ChartContainer = ({ title, children, limit, onLimitChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 3,
        backgroundColor: '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'box-shadow 0.3s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontSize: isMobile ? '0.9rem' : '1.1rem',
          fontWeight: 600,
          color: 'black',
          textAlign: 'center',
        }}
      >
        {title}
      </Typography>
      {onLimitChange && (
        <FormControl size="small" sx={{ width: isMobile ? 80 : 100, mb: 2 }}>
          <InputLabel id="limit-select">Mostrar</InputLabel>
          <Select
            labelId="limit-select"
            value={limit}
            label="Mostrar"
            onChange={(e) => onLimitChange(e.target.value)}
            sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}
          >
            {LIMIT_OPTIONS.map((option) => (
              <MenuItem key={option} value={option} sx={{ fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                {option === 'all' ? 'Todos' : option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Box sx={{ flexGrow: 1, width: '100%', minHeight: 200 }}>{children}</Box>
    </Paper>
  );
};

const GraficPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [dataTypeBusiness, setDataTypeBusiness] = useState([]);
  const [dataBusinessSize, setDataBusinessSize] = useState([]);
  const [dataLicense, setDataLicense] = useState([]);
  const [dataUserSector, setDataUserSector] = useState([]);
  const [limitTypeBusiness, setLimitTypeBusiness] = useState(5);
  const [limitBusinessSize, setLimitBusinessSize] = useState(5);
  const [limitUserSector, setLimitUserSector] = useState(5);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [type, size, license, sector] = await Promise.all([
          api.get('type-business/graphics/type'),
          api.get('business-size/graphics/users'),
          api.get('economic-agents/graphics/license'),
          api.get('sector-activities/graphics/user'),
        ]);

        setDataTypeBusiness(type.data);
        setDataBusinessSize(size.data);
        setDataLicense(license.data);
        setDataUserSector(sector.data);
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
      }
    };
    fetchData();
  }, []);

  const limitedDataTypeBusiness = useMemo(
    () => (limitTypeBusiness === 'all' ? dataTypeBusiness : dataTypeBusiness.slice(0, limitTypeBusiness)),
    [dataTypeBusiness, limitTypeBusiness]
  );

  const limitedDataBusinessSize = useMemo(
    () => (limitBusinessSize === 'all' ? dataBusinessSize : dataBusinessSize.slice(0, limitBusinessSize)),
    [dataBusinessSize, limitBusinessSize]
  );

  const limitedDataUserSector = useMemo(
    () => (limitUserSector === 'all' ? dataUserSector : dataUserSector.slice(0, limitUserSector)),
    [dataUserSector, limitUserSector]
  );

  const renderBarChart = (data, title) => {
    const hasValidData = data.some((d) => d.userCount > 0);

    if (!hasValidData) {
      return (
        <Typography variant="body2" color="textSecondary" textAlign="center">
          Sem dados disponíveis para este gráfico.
        </Typography>
      );
    }

    const series = data.map((d) => ({
      data: [d.userCount],
      label: d.name,
      valueFormatter: (value) => `${value}`,
    }));

    return (
      <BarChart
        xAxis={[{ id: 'categories', data: [`${title}`], scaleType: 'band', axisLabel: { color: 'black', fontSize: isMobile ? 8 : 10 } }]}
        series={series.map((seriesItem) => ({
          ...seriesItem,
          labelMarkType: 'circle',
          valueLabelProps: {
            color: 'black',
            fontSize: isMobile ? 8 : 10,
          },
        }))}
        margin={{ left: -20, bottom: isMobile ? 40 : 60 }}
        height={isMobile ? 200 : isTablet ? 250 : 300}
        width={isMobile ? 180 : isTablet ? 280 : 350}
        slotProps={{
          legend: {
            direction: 'row',
            position: { vertical: 'bottom', horizontal: 'center' },
            labelStyle: {
              fontSize: isMobile ? 8 : 10,
              fill: 'black',
            },
          },
          xAxis: {
            labelStyle: {
              fontSize: isMobile ? 8 : 10,
              fill: 'black',
            },
          },
          yAxis: {
            labelStyle: {
              fontSize: isMobile ? 8 : 10,
              fill: 'black',
            },
          },
        }}
      />
    );
  };

  const alvaraStats = dataLicense.reduce(
    (acc, item) => {
      item.hasLicense ? acc.com++ : acc.sem++;
      return acc;
    },
    { com: 0, sem: 0 }
  );

  const pieData = [
    { id: 0, value: alvaraStats.com, label: 'Com Alvará', arcLabel: `${alvaraStats.com}` },
    { id: 1, value: alvaraStats.sem, label: 'Sem Alvará', arcLabel: `${alvaraStats.sem}` },
  ];

  return (
    <Container sx={{ mt: 0, mb: 4 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{
          fontWeight: 700,
          textAlign: 'center',
          color: theme.palette.success.main,
          mb: 3,
          fontSize: isMobile ? '1.5rem' : '1.8rem',
        }}
      >
        Painel de Estatísticas de Empreendedores
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={6} lg={6}>
          <ChartContainer
            title="Empreendedores por Tipo de Negócio"
            limit={limitTypeBusiness}
            onLimitChange={setLimitTypeBusiness}
          >
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderBarChart(limitedDataTypeBusiness, 'Tipos de Empreendimento')}
            </Box>
          </ChartContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={6} lg={6}>
          <ChartContainer
            title="Empreendedores Por Porte"
            limit={limitBusinessSize}
            onLimitChange={setLimitBusinessSize}
          >
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderBarChart(limitedDataBusinessSize, 'Modalidades')}
            </Box>
          </ChartContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={4} lg={4}>
          <ChartContainer title="Empreendedores com e sem Alvará">
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {pieData.some(item => item.value > 0) ? (
                <PieChart
                  series={[
                    {
                      data: pieData,
                      innerRadius: isMobile ? 30 : 40,
                      outerRadius: isMobile ? 70 : 90,
                      highlightScope: { fade: 'global', highlight: 'item' },
                      faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                      arcLabel: (item) => `${item.value}`,
                      arcLabelMinAngle: 45,
                    },
                  ]}
                  width={isMobile ? 250 : isTablet ? 300 : 350}
                  height={isMobile ? 200 : 250}
                  slotProps={{
                    legend: {
                      direction: 'horizontal',
                      position: { vertical: 'bottom', horizontal: 'center' },
                      labelStyle: { fontSize: isMobile ? 10 : 12, color: 'black' },
                    },
                    label: {
                      textAnchor: 'middle',
                      style: { fontSize: isMobile ? 10 : 12, fill: 'black' },
                    },
                  }}
                />
              ) : (
                <Typography variant="body2" color="textSecondary" textAlign="center">
                  Sem dados disponíveis para este gráfico.
                </Typography>
              )}
            </Box>
          </ChartContainer>
        </Grid>

        <Grid item xs={12} sm={6} md={8} lg={8}>
          <ChartContainer
            title="Empreendedores por Setor de Atuação"
            limit={limitUserSector}
            onLimitChange={setLimitUserSector}
          >
            <Box sx={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {renderBarChart(limitedDataUserSector, 'Setores')}
            </Box>
          </ChartContainer>
        </Grid>
      </Grid>
    </Container>
  );
};

export default GraficPage;